import { useCallback, useEffect, useState } from 'react'
import { api } from '../../api'
import { usePlatform } from './PlatformLayout'
import { Badge, Empty, Icon, P, PageHeader, ago, type Tone } from './ui'

/** One audit event as the platform sees it: who, what kind of record, where, when. Never the contents. */
export interface AuditEvent {
  id: string
  at: string
  action: 'create' | 'update' | 'soft_delete' | 'restore' | 'login' | 'logout'
  entity: string
  clinic: string | null
  user_name: string | null
  user_role: string | null
  ip: string | null
}

const ENTITY: Record<string, string> = {
  patients: 'a patient', appointments: 'a booking', visits: 'a visit', payments: 'a payment', invoices: 'a bill',
  prescriptions: 'a prescription', users: 'a staff login', doctors: 'a doctor', clinics: 'a clinic', lab_bookings: 'a lab booking',
  stock_items: 'a stock item', test_stock_items: 'a test’s items', clinic_settings: 'clinic settings', sessions: 'a session',
  test_master: 'a lab test', drug_master: 'a medicine', backups: 'a backup',
}

export function eventLabel(e: AuditEvent): { text: string; icon: string; tone: Tone } {
  const what = ENTITY[e.entity] ?? e.entity.replace(/_/g, ' ')
  switch (e.action) {
    case 'login': return { text: 'signed in', icon: P.login, tone: 'info' }
    case 'logout': return { text: 'signed out', icon: P.logout, tone: 'neutral' }
    case 'create': return { text: `added ${what}`, icon: P.plus, tone: 'success' }
    case 'soft_delete': return { text: `removed ${what}`, icon: P.trash, tone: 'danger' }
    case 'restore': return { text: `restored ${what}`, icon: P.restore, tone: 'warning' }
    default: return { text: `changed ${what}`, icon: P.edit, tone: 'primary' }
  }
}

const ACTIONS: [string, string][] = [['', 'All'], ['create', 'Added'], ['update', 'Changed'], ['soft_delete', 'Removed'], ['login', 'Sign-ins']]

export default function Activity() {
  const { clinics } = usePlatform()
  const [clinicId, setClinicId] = useState('')
  const [action, setAction] = useState('')
  const [events, setEvents] = useState<AuditEvent[] | null>(null)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    setEvents(null)
    const p = new URLSearchParams({ limit: '100' })
    if (clinicId) p.set('clinicId', clinicId)
    if (action) p.set('action', action)
    try {
      setEvents((await api.get<{ events: AuditEvent[] }>(`/api/platform/audit?${p}`)).events)
    } catch (e) {
      setErr((e as Error).message); setEvents([])
    }
  }, [clinicId, action])
  useEffect(() => { void load() }, [load])

  return (
    <>
      <PageHeader crumbs={['Platform', 'Operations', 'Activity log']} title="Activity log"
        description="Who did what, in which clinic, and when. Record contents are never shown here."
        actions={<button type="button" className="pa-btn" onClick={() => void load()}><Icon d={P.refresh} />Refresh</button>} />
      {err && <div className="pa-alert error">{err}</div>}
      <div className="pa-card">
        <div className="pa-toolbar">
          <div className="pa-tabs" role="tablist" aria-label="Action">
            {ACTIONS.map(([id, label]) => (
              <button key={id || 'all'} type="button" role="tab" aria-selected={action === id} className={action === id ? 'on' : ''} onClick={() => setAction(id)}>{label}</button>
            ))}
          </div>
          <select className="pa-select" style={{ width: 240, marginLeft: 'auto' }} aria-label="Clinic" value={clinicId} onChange={(e) => setClinicId(e.target.value)}>
            <option value="">All clinics</option>
            {(clinics ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="pa-table-wrap">
          <table className="pa-table">
            <thead><tr><th>When</th><th>Who</th><th>What</th><th>Clinic</th><th>From</th></tr></thead>
            <tbody>
              {events === null && [0, 1, 2, 3, 4].map((i) => <tr key={i}><td colSpan={5}><div className="pa-skel" style={{ height: 18 }} /></td></tr>)}
              {events?.map((e) => {
                const l = eventLabel(e)
                return (
                  <tr key={e.id}>
                    <td className="tnum" title={new Date(e.at).toLocaleString('en-IN')} style={{ whiteSpace: 'nowrap' }}>{ago(e.at)}</td>
                    <td><div className="pa-cell"><div><b>{e.user_name ?? 'System'}</b><small>{e.user_role?.replace('_', ' ') ?? '—'}</small></div></div></td>
                    <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Badge tone={l.tone}><Icon d={l.icon} size={11} />{e.action.replace('_', ' ')}</Badge>{l.text}</span></td>
                    <td>{e.clinic ?? <span className="subtle">Platform</span>}</td>
                    <td className="pa-code subtle">{e.ip ?? '—'}</td>
                  </tr>
                )
              })}
              {events?.length === 0 && <tr><td colSpan={5}><Empty icon={P.list} title="No activity">Nothing matches these filters yet.</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
